const SITE_URL = "https://przyjazne-deklaracje.pl";

const HTML_ENTITIES = Object.freeze({
	amp: "&",
	apos: "'",
	gt: ">",
	hellip: "…",
	laquo: "«",
	ldquo: "“",
	lsquo: "‘",
	lt: "<",
	mdash: "—",
	nbsp: " ",
	ndash: "–",
	quot: '"',
	raquo: "»",
	rdquo: "”",
	rsquo: "’",
});

function decodeEntity(entity) {
	if (!entity) return "";

	if (entity[0] === "#") {
		const isHex = entity[1]?.toLowerCase() === "x";
		const raw = entity.slice(isHex ? 2 : 1);
		const codePoint = Number.parseInt(raw, isHex ? 16 : 10);

		if (Number.isInteger(codePoint) && codePoint >= 0 && codePoint <= 0x10ffff) {
			try {
				return String.fromCodePoint(codePoint);
			} catch {
				return `&${entity};`;
			}
		}
	}

	return HTML_ENTITIES[entity.toLowerCase()] ?? `&${entity};`;
}

export function decodeHtmlEntities(value) {
	return String(value || "").replace(/&(#x?[0-9a-f]+|[a-z][a-z0-9]+);/gi, (_, entity) =>
		decodeEntity(entity),
	);
}

export function normalizeWhitespace(value) {
	return String(value || "")
		.replace(/\u00a0/g, " ")
		.replace(/\s+/g, " ")
		.trim();
}

export function htmlToText(value) {
	const withoutNonContent = String(value || "")
		.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, " ")
		.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, " ")
		.replace(/<br\s*\/?>/gi, " ")
		.replace(/<hr\s*\/?>/gi, " ")
		.replace(
			/<\/?\s*(?:p|li|div|ul|ol|section|article|h[1-6]|blockquote|pre|table|thead|tbody|tfoot|tr|td|th|dl|dt|dd)\b[^>]*>/gi,
			" ",
		)
		// Inline HTML nie tworzy własnej spacji w renderowanym tekście.
		// Usuwamy więc sam znacznik zamiast zastępować każdy tag spacją.
		.replace(/<[^>]+>/g, "");

	return normalizeWhitespace(decodeHtmlEntities(withoutNonContent));
}

export function markdownToText(value) {
	const markdown = String(value || "")
		.replace(/```[\s\S]*?```/g, " ")
		.replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
		.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
		.replace(/<https?:\/\/[^>]+>/g, " ")
		.replace(/^\s{0,3}#{1,6}\s+/gm, "")
		.replace(/^\s*>\s?/gm, "")
		.replace(/^\s*[-*+]\s+/gm, "")
		.replace(/^\s*\d+[.)]\s+/gm, "")
		.replace(/[*_~`]+/g, "");

	return htmlToText(markdown);
}

export function normalizeFaqItems(items) {
	const result = [];
	const seen = new Set();

	for (const item of Array.isArray(items) ? items : []) {
		const question = htmlToText(item?.question ?? item?.name ?? item?.title ?? "");
		const answerText = htmlToText(
			item?.answerText ?? item?.answerHtml ?? item?.answer ?? item?.text ?? "",
		);

		if (!question || !answerText) {
			continue;
		}

		const dedupeKey = `${question}\u0000${answerText}`;
		if (seen.has(dedupeKey)) {
			continue;
		}

		seen.add(dedupeKey);
		result.push({
			...item,
			question,
			answerText,
		});
	}

	return result;
}

export function getFaqItemsFromSection({ faqRoot, section, translate }) {
	const faqSection = faqRoot?.[section];

	if (!Array.isArray(faqSection) || typeof translate !== "function") {
		return [];
	}

	return normalizeFaqItems(
		faqSection.map((faq, index) => {
			const question = translate(faq.title);
			const answerHtml = translate(faq.tekst);

			return {
				key: `${section}-${index}`,
				question,
				answerHtml,
				answerText: htmlToText(answerHtml),
				titleId: faq.title,
				answerId: faq.tekst,
			};
		}),
	);
}

export function normalizeFaqPageUrl(value) {
	const raw = String(value || "/").trim() || "/";
	const absolute = /^https?:\/\//i.test(raw)
		? raw
		: `${SITE_URL}${raw.startsWith("/") ? "" : "/"}${raw}`;

	try {
		const url = new URL(absolute);
		url.search = "";
		url.hash = "";

		if (url.pathname !== "/" && !url.pathname.endsWith("/")) {
			url.pathname = `${url.pathname}/`;
		}

		return url.toString();
	} catch {
		return `${SITE_URL}/`;
	}
}

export function buildFaqPageSchema(items, pageUrl) {
	const normalizedItems = normalizeFaqItems(items);

	if (!normalizedItems.length) {
		return null;
	}

	const canonicalUrl = normalizeFaqPageUrl(pageUrl);

	return {
		"@context": "https://schema.org",
		"@type": "FAQPage",
		"@id": `${canonicalUrl}#faq`,
		url: canonicalUrl,
		mainEntity: normalizedItems.map((item) => ({
			"@type": "Question",
			name: item.question,
			acceptedAnswer: {
				"@type": "Answer",
				text: item.answerText,
			},
		})),
	};
}

export function serializeJsonLd(data) {
	return JSON.stringify(data)
		.replace(/</g, "\\u003c")
		.replace(/\u2028/g, "\\u2028")
		.replace(/\u2029/g, "\\u2029");
}
